import React from 'react';

import { Box, Flex, Space } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useMutation } from '@tanstack/react-query';
import { BubbleButton } from 'components/SmartAssistantComponents/BubbleButton';
import { BubbleTextInput } from 'components/SmartAssistantComponents/BubbleTextInput';
import { MessageBubble, MessageBubbleProps } from 'components/SmartAssistantComponents/MessageBubble';
import { useLeadFormVisibility } from 'hooks/use-lead-form-visibility';
import { LeadsService } from 'services/leads-service';
import { ViewMode } from 'shared/enums/ViewMode';
import { ChatMessage } from 'shared/interfaces/ChatMessage';
import { CollectLeadInfoToolConfig, InfoType } from 'shared/interfaces/tools/CollectLeadsInfo';
import { ToolConfig, ToolKind } from 'shared/interfaces/tools/Tool';
import { DEFAULT_MAX_LENGTH, emailValidator, requiredInputValidator } from 'shared/utils/form-validators';

interface LeadFormBubbleProps extends MessageBubbleProps {
  smartAssistantId?: string;
  toolConfigs?: ToolConfig[];
  addNewMessageToConversation?: (newMessage: ChatMessage) => void;
  viewMode: ViewMode;
}

export function LeadFormBubble({
  smartAssistantId,
  toolConfigs,
  addNewMessageToConversation,
  viewMode,
  ...messageBubbleProps
}: LeadFormBubbleProps) {
  const [, setShowLeadForm] = useLeadFormVisibility(smartAssistantId);
  const collectLeadInfoToolConfig = toolConfigs?.find(
    (toolConfig) => toolConfig.kind === ToolKind.COLLECT_LEAD_INFO
  ) as CollectLeadInfoToolConfig | undefined;

  const leadForm = useForm({
    initialValues: {
      name: '',
      email: '',
      phone: ''
    },
    validate: {
      email: (value) =>
        collectLeadInfoToolConfig?.infoTypesToCollect.includes(InfoType.EMAIL)
          ? requiredInputValidator(value) || emailValidator(value)
          : null,
      name: (value) =>
        collectLeadInfoToolConfig?.infoTypesToCollect.includes(InfoType.NAME) ? requiredInputValidator(value) : null,
      phone: (value) =>
        collectLeadInfoToolConfig?.infoTypesToCollect.includes(InfoType.PHONE) ? requiredInputValidator(value) : null
    }
  });

  const submitLeadFormMutation = useMutation({
    mutationFn: LeadsService.saveLeadInformation,
    onSuccess: () => {
      setShowLeadForm(false);
      if (collectLeadInfoToolConfig) {
        addNewMessageToConversation?.({
          fromUser: false,
          text: collectLeadInfoToolConfig.collectionAcknowledgementMessage
        });
      }
    },
    onError: () => {
      setShowLeadForm(false);
    }
  });

  if (!collectLeadInfoToolConfig) return null;

  return (
    <MessageBubble
      message={{ text: collectLeadInfoToolConfig?.formTitle, fromUser: false }}
      w={310}
      onClose={() => viewMode === 'live' && setShowLeadForm(false)}
      {...messageBubbleProps}>
      <Flex direction="column" align="flex-start" mt="xs">
        {collectLeadInfoToolConfig.infoTypesToCollect.map((infoType) => (
          <Box key={infoType} w="100%" mb={10}>
            <BubbleTextInput
              smartAssistantVisualConfig={messageBubbleProps.smartAssistantVisualConfig}
              maxLength={DEFAULT_MAX_LENGTH}
              label={collectLeadInfoToolConfig.labels[infoType] || ''}
              {...leadForm.getInputProps(infoType)}
            />
          </Box>
        ))}

        <Space h={10} />

        <Flex direction="row" justify="flex-end" w="100%">
          <BubbleButton
            smartAssistantVisualConfig={messageBubbleProps.smartAssistantVisualConfig}
            onClick={() => {
              if (collectLeadInfoToolConfig && viewMode === 'live' && smartAssistantId) {
                const leadFormValidation = leadForm.validate();
                if (leadFormValidation.hasErrors) return;

                submitLeadFormMutation.mutate({
                  toolConfig: {
                    ...collectLeadInfoToolConfig,
                    smartAssistantId
                  },
                  toolExecutionParams: {
                    email: leadForm.values.email,
                    name: leadForm.values.name,
                    phone: leadForm.values.phone
                  },
                  smartAssistantId: smartAssistantId
                });
              }
            }}>
            {collectLeadInfoToolConfig.submitButtonLabel}
          </BubbleButton>
        </Flex>
      </Flex>
    </MessageBubble>
  );
}
